import React, { useState } from 'react';
import useSWR from 'swr';
import {
  Container, Paper, Typography, Box, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, CircularProgress, Alert,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Tabs, Tab, Snackbar, Avatar, Stack, Tooltip,
  Select, MenuItem, FormControl, InputLabel, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import {
  CheckCircle as ResolveIcon,
  Cancel as DismissIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  Flag as FlagIcon,
  FlagOutlined as UnflagIcon,
  Announcement as AnnouncementIcon,
  Report as ReportIcon,
  Gavel as ModerateIcon,
} from '@mui/icons-material';
import { axiosService, fetcher } from 'shared/helpers/axios';
import BackButton from 'components/common/buttons/BackButton';
import PageContainer from 'components/common/layout/PageContainer';

const priorityColors = { 1: 'error', 2: 'warning', 3: 'info', 4: 'default' };
const priorityLabels = { 1: 'Critical', 2: 'High', 3: 'Medium', 4: 'Low' };
const statusColors = { pending: 'warning', reviewing: 'info', resolved: 'success', dismissed: 'default' };

// ── Flagged Announcements panel ───────────────────────────────────────────────
function FlaggedAnnouncementsPanel() {
  const { data, isLoading, error, mutate } = useSWR(
    '/interact/posts/?is_flagged=true&is_announcement=true&ordering=-updated&moderate=1',
    fetcher,
  );
  const [unflagging, setUnflagging] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [preview, setPreview] = useState({ open: false, post: null });

  const posts = data?.results || data || [];

  const handleUnflag = async (post) => {
    setUnflagging(post.id);
    try {
      await axiosService.post(`/interact/posts/${post.id}/unflag/?moderate=1`);
      mutate();
      setSnackbar({ open: true, message: 'Flag removed — announcement restored to feed.', severity: 'success' });
      if (preview.post?.id === post.id) setPreview({ open: false, post: null });
    } catch {
      setSnackbar({ open: true, message: 'Failed to remove flag.', severity: 'error' });
    } finally {
      setUnflagging(null);
    }
  };

  if (isLoading) return (
    <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
  );
  if (error) return (
    <Alert severity="error">Failed to load flagged announcements.</Alert>
  );

  return (
    <>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <FlagIcon color="warning" />
        <Typography variant="h6" fontWeight={700}>Flagged Announcements</Typography>
        <Chip label={posts.length} size="small" color="warning" />
      </Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        These announcements were flagged as irrelevant and hidden from the feed. Remove the flag to restore them.
      </Typography>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Post</TableCell>
              <TableCell>Author</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>Last Updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {posts.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                  <Typography color="text.secondary">No flagged announcements</Typography>
                </TableCell>
              </TableRow>
            )}
            {posts.map(post => (
              <TableRow key={post.id} hover>
                <TableCell sx={{ maxWidth: 420 }}>
                  {post.title && (
                    <Typography variant="body2" fontWeight={600} noWrap>{post.title}</Typography>
                  )}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {post.body}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar
                      src={post.author_details?.avatar}
                      sx={{ width: 24, height: 24, fontSize: '0.7rem' }}
                    >
                      {post.author_details?.first_name?.[0]}
                    </Avatar>
                    <Typography variant="body2" noWrap>
                      {post.author_details
                        ? `${post.author_details.first_name} ${post.author_details.last_name}`
                        : post.author_name || 'Unknown'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {new Date(post.updated || post.created).toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title="Preview post">
                    <IconButton size="small" onClick={() => setPreview({ open: true, post })}>
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove flag — restore to feed">
                    <span>
                      <IconButton
                        size="small"
                        color="warning"
                        onClick={() => handleUnflag(post)}
                        disabled={unflagging === post.id}
                      >
                        {unflagging === post.id
                          ? <CircularProgress size={16} />
                          : <UnflagIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Post preview dialog */}
      <Dialog open={preview.open} onClose={() => setPreview({ open: false, post: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <FlagIcon color="warning" fontSize="small" />
            Flagged Announcement
          </Box>
          <IconButton size="small" onClick={() => setPreview({ open: false, post: null })}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        {preview.post && (
          <DialogContent dividers>
            <Stack spacing={2}>
              {preview.post.title && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Title</Typography>
                  <Typography variant="body1" fontWeight={600}>{preview.post.title}</Typography>
                </Box>
              )}
              <Box>
                <Typography variant="caption" color="text.secondary">Body</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{preview.post.body}</Typography>
              </Box>
              <Box display="flex" gap={2} flexWrap="wrap">
                <Box>
                  <Typography variant="caption" color="text.secondary">Author</Typography>
                  <Typography variant="body2">
                    {preview.post.author_details
                      ? `${preview.post.author_details.first_name} ${preview.post.author_details.last_name}`
                      : preview.post.author_name || 'Unknown'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Posted</Typography>
                  <Typography variant="body2">{new Date(preview.post.created).toLocaleString()}</Typography>
                </Box>
              </Box>
            </Stack>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setPreview({ open: false, post: null })}>Close</Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={unflagging === preview.post?.id ? <CircularProgress size={16} /> : <UnflagIcon />}
            disabled={unflagging === preview.post?.id}
            onClick={() => handleUnflag(preview.post)}
          >
            Remove Flag
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
const AdminReportsPage = () => {
  const [section, setSection] = useState('reports');
  const [tab, setTab] = useState(0);
  const [detailDialog, setDetailDialog] = useState({ open: false, report: null });
  const [resolveDialog, setResolveDialog] = useState({ open: false, report: null, action: '' });
  const [resolutionNote, setResolutionNote] = useState('');
  const [processing, setProcessing] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [categoryFilter, setCategoryFilter] = useState('');
  const [moderationNote, setModerationNote] = useState('');

  // A reported post: remove it (its author still sees it, with the note; can be restored), restore it, delete it
  // for good, or keep it (dismiss). Every open report on the post is settled at once; the author and the
  // reporters are notified (interact/report/viewsets.py moderate).
  const handleModerate = async (report, action) => {
    if (action === 'delete' && !window.confirm('Delete this post for good? This cannot be undone. Removing it hides it but keeps it.')) return;
    try {
      setProcessing(report.id);
      const { data: updated } = await axiosService.post(`/interact/reports/${report.id}/moderate/`, { action, note: moderationNote });
      const done = { remove: 'Post removed', restore: 'Post restored', delete: 'Post deleted', dismiss: 'Report dismissed: the post stays' }[action];
      setSnackbar({ open: true, message: done, severity: 'success' });
      setDetailDialog({ open: true, report: updated });
      setModerationNote('');
      mutate();
    } catch (e) {
      setSnackbar({ open: true, message: e.response?.data?.detail || 'Action failed', severity: 'error' });
    } finally {
      setProcessing(null);
    }
  };

  const { data, error, isLoading, mutate } = useSWR('/interact/reports/', fetcher);

  const allReports = data?.results || data || [];
  const filteredByCategory = categoryFilter
    ? allReports.filter(r => r.category === categoryFilter)
    : allReports;

  const tabs = [
    { label: 'Pending', filter: r => r.status === 'pending' },
    { label: 'Reviewing', filter: r => r.status === 'reviewing' },
    { label: 'Resolved', filter: r => r.status === 'resolved' || r.status === 'dismissed' },
    { label: 'All', filter: () => true },
  ];

  const reports = filteredByCategory.filter(tabs[tab].filter);

  const handleResolve = async (action) => {
    const { report } = resolveDialog;
    if (!report) return;
    try {
      setProcessing(report.id);
      await axiosService.patch(`/interact/reports/${report.id}/resolve/`, {
        status: action,
        resolution_note: resolutionNote,
      });
      setSnackbar({ open: true, message: `Report ${action} successfully`, severity: 'success' });
      setResolveDialog({ open: false, report: null, action: '' });
      setResolutionNote('');
      mutate();
    } catch (e) {
      setSnackbar({ open: true, message: e.response?.data?.detail || 'Action failed', severity: 'error' });
    } finally {
      setProcessing(null);
    }
  };

  const handleStatusChange = async (reportId, newStatus) => {
    try {
      setProcessing(reportId);
      await axiosService.patch(`/interact/reports/${reportId}/`, { status: newStatus });
      mutate();
    } catch (e) {
      setSnackbar({ open: true, message: 'Status update failed', severity: 'error' });
    } finally {
      setProcessing(null);
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">Failed to load reports. Make sure you have admin access.</Alert>
      </Container>
    );
  }

  return (
    <PageContainer width="wide">
      <Box display="flex" alignItems="center" gap={1} mb={3}>
        <BackButton position="inline" />
        <Typography variant="h4" fontWeight={800}>Reports & Moderation</Typography>
      </Box>

      {/* Section toggle */}
      <ToggleButtonGroup
        value={section}
        exclusive
        onChange={(_, v) => { if (v) setSection(v); }}
        size="small"
        sx={{ mb: 3 }}
      >
        <ToggleButton value="reports">
          <ReportIcon sx={{ mr: 1, fontSize: 18 }} />
          User Reports
        </ToggleButton>
        <ToggleButton value="flagged">
          <FlagIcon sx={{ mr: 1, fontSize: 18 }} />
          Flagged Announcements
        </ToggleButton>
      </ToggleButtonGroup>

      {/* ── Flagged announcements section ── */}
      {section === 'flagged' && <FlaggedAnnouncementsPanel />}

      {/* ── User reports section ── */}
      {section === 'reports' && (
        <>
          {/* Stats row */}
          <Stack direction="row" spacing={2} mb={3}>
            {['pending', 'reviewing', 'resolved', 'dismissed'].map(s => {
              const count = allReports.filter(r => r.status === s).length;
              return (
                <Paper key={s} sx={{ px: 3, py: 2, flex: 1, textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight={700}>{count}</Typography>
                  <Typography variant="body2" color="text.secondary" textTransform="capitalize">{s}</Typography>
                </Paper>
              );
            })}
          </Stack>

          {/* Filters */}
          <Box display="flex" gap={2} mb={2} alignItems="center">
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ flex: 1 }}>
              {tabs.map((t, i) => (
                <Tab key={i} label={`${t.label} (${filteredByCategory.filter(t.filter).length})`} />
              ))}
            </Tabs>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Category</InputLabel>
              <Select value={categoryFilter} label="Category" onChange={e => setCategoryFilter(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="abuse">Abuse</MenuItem>
                <MenuItem value="spam">Spam</MenuItem>
                <MenuItem value="copyright">Copyright</MenuItem>
                <MenuItem value="privacy">Privacy</MenuItem>
                <MenuItem value="misinformation">Misinformation</MenuItem>
                <MenuItem value="complaint">Complaint</MenuItem>
                <MenuItem value="bug">Bug</MenuItem>
                <MenuItem value="request">Request</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Table */}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Priority</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Reporter</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reports.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">No reports found</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {reports.map(report => (
                  <TableRow key={report.id} hover>
                    <TableCell>
                      <Chip
                        size="small"
                        label={priorityLabels[report.priority] || 'Low'}
                        color={priorityColors[report.priority] || 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={report.category_display || report.category} variant="outlined" />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {report.subject}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar
                          src={report.reporter_details?.avatar}
                          alt={report.reporter_details ? `${report.reporter_details.first_name} ${report.reporter_details.last_name}` : 'Reporter'}
                          sx={{ width: 24, height: 24, fontSize: '0.75rem' }}
                        >
                          {report.reporter_details?.first_name?.[0]}
                        </Avatar>
                        <Typography variant="body2">
                          {report.reporter_details ? `${report.reporter_details.first_name} ${report.reporter_details.last_name}` : 'Unknown'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={report.status_display || report.status}
                        color={statusColors[report.status] || 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {new Date(report.created).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View details">
                        <IconButton size="small" onClick={() => setDetailDialog({ open: true, report })} aria-label="View report details">
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {report.target_type === 'post' && report.target_preview?.exists && (
                        <Tooltip title={report.target_preview.removed ? 'Post removed: review' : 'Moderate the post'}>
                          <IconButton size="small" color={report.target_preview.removed ? 'default' : 'warning'}
                            onClick={() => { setModerationNote(''); setDetailDialog({ open: true, report }); }} aria-label="Moderate the post">
                            <ModerateIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {(report.status === 'pending' || report.status === 'reviewing') && (
                        <>
                          <Tooltip title="Resolve">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => { setResolveDialog({ open: true, report, action: 'resolved' }); setResolutionNote(''); }}
                              disabled={processing === report.id}
                              aria-label="Resolve report"
                            >
                              <ResolveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Dismiss">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => { setResolveDialog({ open: true, report, action: 'dismissed' }); setResolutionNote(''); }}
                              disabled={processing === report.id}
                              aria-label="Dismiss report"
                            >
                              <DismissIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {report.status === 'pending' && (
                        <Tooltip title="Mark as Reviewing">
                          <Button
                            size="small"
                            variant="text"
                            sx={{ ml: 1, textTransform: 'none', fontSize: '0.75rem' }}
                            onClick={() => handleStatusChange(report.id, 'reviewing')}
                            disabled={processing === report.id}
                          >
                            Review
                          </Button>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Detail Dialog */}
      <Dialog open={detailDialog.open} onClose={() => setDetailDialog({ open: false, report: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Report Details
          <IconButton onClick={() => setDetailDialog({ open: false, report: null })} aria-label="Close report details"><CloseIcon /></IconButton>
        </DialogTitle>
        {detailDialog.report && (
          <DialogContent dividers>
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">Subject</Typography>
                <Typography variant="body1" fontWeight={600}>{detailDialog.report.subject}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Message</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{detailDialog.report.message}</Typography>
              </Box>
              <Box display="flex" gap={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Category</Typography>
                  <Box><Chip size="small" label={detailDialog.report.category_display} /></Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Priority</Typography>
                  <Box>
                    <Chip size="small" label={priorityLabels[detailDialog.report.priority]} color={priorityColors[detailDialog.report.priority]} />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box>
                    <Chip size="small" label={detailDialog.report.status_display} color={statusColors[detailDialog.report.status]} />
                  </Box>
                </Box>
              </Box>
              {detailDialog.report.target_type && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Reported Entity</Typography>
                  <Typography variant="body2">
                    {detailDialog.report.target_type} — {detailDialog.report.object_id}
                    {detailDialog.report.target_type === 'reflection' && (
                      <> · <a href={`/reflections/${detailDialog.report.object_id}/`} target="_blank" rel="noopener noreferrer">open the reflection</a></>
                    )}
                  </Typography>
                </Box>
              )}
              {/* the reported post itself, and what to do with it */}
              {detailDialog.report.target_type === 'post' && detailDialog.report.target_preview && (() => {
                const p = detailDialog.report.target_preview;
                const busy = processing === detailDialog.report.id;
                if (!p.exists) {
                  return <Alert severity="info">The post no longer exists.</Alert>;
                }
                return (
                  <Box sx={{ p: 2, border: '1px solid', borderColor: p.removed ? 'warning.light' : 'divider', borderRadius: 2 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
                      <Typography variant="subtitle2" fontWeight={700}>Reported post</Typography>
                      <Chip size="small" variant="outlined" label={p.visibility} />
                      {p.removed && <Chip size="small" color="warning" label="Removed" />}
                      <Box flex={1} />
                      <a href={p.url} target="_blank" rel="noopener noreferrer">open the post</a>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      by {p.author_name} · {new Date(p.created).toLocaleString()}
                    </Typography>
                    {p.title && <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>{p.title}</Typography>}
                    <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', maxHeight: 200, overflowY: 'auto' }}>{p.excerpt}</Typography>
                    {p.removed && p.removal_reason && (
                      <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 1 }}>Removed: {p.removal_reason}</Typography>
                    )}
                    <TextField fullWidth size="small" multiline minRows={2} sx={{ mt: 2 }} value={moderationNote}
                      onChange={(e) => setModerationNote(e.target.value)}
                      label={p.removed ? 'Note (optional)' : 'Reason, shown to the author (optional)'} />
                    <Box display="flex" gap={1} mt={1.5} flexWrap="wrap">
                      {p.removed ? (
                        <Button size="small" variant="outlined" disabled={busy} onClick={() => handleModerate(detailDialog.report, 'restore')}>
                          Restore post
                        </Button>
                      ) : (
                        <Button size="small" variant="contained" color="warning" disabled={busy}
                          onClick={() => handleModerate(detailDialog.report, 'remove')}>
                          Remove post
                        </Button>
                      )}
                      <Button size="small" variant="outlined" color="error" disabled={busy}
                        onClick={() => handleModerate(detailDialog.report, 'delete')}>
                        Delete permanently
                      </Button>
                      {!p.removed && ['pending', 'reviewing'].includes(detailDialog.report.status) && (
                        <Button size="small" disabled={busy} onClick={() => handleModerate(detailDialog.report, 'dismiss')}>
                          Keep the post (dismiss)
                        </Button>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      Removing hides the post from everyone but its author, who sees why, and can be undone. Every open
                      report on this post is settled, and the author and the reporters are notified.
                    </Typography>
                  </Box>
                );
              })()}
              <Box>
                <Typography variant="caption" color="text.secondary">Reporter</Typography>
                <Box display="flex" gap={1} alignItems="center" mt={0.5}>
                  <Avatar src={detailDialog.report.reporter_details?.avatar} alt={`${detailDialog.report.reporter_details?.first_name || ''} ${detailDialog.report.reporter_details?.last_name || ''}`} sx={{ width: 28, height: 28 }}>
                    {detailDialog.report.reporter_details?.first_name?.[0]}
                  </Avatar>
                  <Typography variant="body2">
                    {detailDialog.report.reporter_details?.first_name} {detailDialog.report.reporter_details?.last_name}
                  </Typography>
                </Box>
              </Box>
              {detailDialog.report.resolution_note && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Resolution Note</Typography>
                  <Typography variant="body2">{detailDialog.report.resolution_note}</Typography>
                </Box>
              )}
              <Box>
                <Typography variant="caption" color="text.secondary">Submitted</Typography>
                <Typography variant="body2">{new Date(detailDialog.report.created).toLocaleString()}</Typography>
              </Box>
            </Stack>
          </DialogContent>
        )}
      </Dialog>

      {/* Resolve/Dismiss Dialog */}
      <Dialog open={resolveDialog.open} onClose={() => setResolveDialog({ open: false, report: null, action: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>
          {resolveDialog.action === 'resolved' ? 'Resolve Report' : 'Dismiss Report'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {resolveDialog.report?.subject}
          </Typography>
          <TextField
            label="Resolution Note (optional)"
            fullWidth
            multiline
            rows={3}
            value={resolutionNote}
            onChange={e => setResolutionNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialog({ open: false, report: null, action: '' })}>Cancel</Button>
          <Button
            variant="contained"
            color={resolveDialog.action === 'resolved' ? 'success' : 'error'}
            onClick={() => handleResolve(resolveDialog.action)}
            disabled={processing === resolveDialog.report?.id}
          >
            {processing === resolveDialog.report?.id ? <CircularProgress size={20} /> : resolveDialog.action === 'resolved' ? 'Resolve' : 'Dismiss'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default AdminReportsPage;
